import { z } from 'zod';
import { genderSchema } from './me';

/** Prvi korak čarobnjaka — osnovni podaci, jedan po jedan ekran. */
export const onboardingBasicsInput = z.object({
  firstName: z.string().min(1, 'Upiši ime').max(80),
  lastName: z.string().min(1, 'Upiši prezime').max(80),
  birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Odaberi datum rođenja'),
  gender: z.enum(['female', 'male', 'other', 'undisclosed']),
  heightCm: z.number().int().min(100).max(250),
  weightKg: z.number().min(30).max(300),
});

export const goalKeys = ['lose_weight', 'tone_up', 'be_active', 'build_muscle', 'feel_better'] as const;
export type GoalKey = (typeof goalKeys)[number];

/** Drugi korak — cilj. Mršavljenje traži i željenu kilažu. */
export const onboardingGoalInput = z
  .object({
    goal: z.enum(goalKeys),
    targetWeightKg: z.number().min(30).max(300).nullable().optional(),
  })
  .refine((v) => v.goal !== 'lose_weight' || typeof v.targetWeightKg === 'number', {
    message: 'Za cilj mršavljenja upiši željenu kilažu',
    path: ['targetWeightKg'],
  });

export interface GoalChoice {
  key: GoalKey;
  label: string;
  needsTargetWeight: boolean;
}

export interface MeasurementGuide {
  /**
   * Video za CIJELO mjerenje. Ostaje kao pomoc kad tocka nema svoj — bolje
   * opca snimka nego nijedna.
   */
  videoUrl: string | null;
  /**
   * Jedna tocka mjerenja (`waist`, `chest`, …): recenica i, kad postoji, svoj
   * video. Uputa se trazi u trenutku kad covjek drzi metar oko struka, pa mora
   * biti uz to polje — jedna zajednicka snimka za svih pet opsega znaci
   * premotavanje do mjesta koje ga zanima.
   */
  steps: { key: string; hr: string; en: string; videoUrl: string | null }[];
}

export interface TrialStatus {
  hasProgram: boolean;
  paid: boolean;
  trial: { used: number; total: number; remaining: number } | null;
  lockedFrom: { workoutId: string; name: string } | null;
}

/**
 * `GET /v1/onboarding/state` — gdje je korisnik stao u čarobnjaku.
 *
 * `completedAt` je vrijeme, ne zastavica: server pamti KAD je upitnik završen,
 * pa se isti podatak koristi i za analitiku. Portal je ovo prije imao kao
 * `{ currentStep, completed }` i time izmislio polje kojeg API nema.
 */
export const onboardingStateOutput = z.object({
  currentStep: z.number().int(),
  completedAt: z.string().nullable(),
  basicsDone: z.boolean(),
  goalDone: z.boolean(),
  programChosen: z.boolean(),
  basics: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      birthDate: z.string().nullable(),
      gender: genderSchema.nullable(),
      heightCm: z.number().int().nullable(),
      weightKg: z.number().nullable(),
    })
    .nullable(),
  goal: z.string().nullable(),
});

export type OnboardingStateOutput = z.infer<typeof onboardingStateOutput>;

export type OnboardingBasicsInput = z.input<typeof onboardingBasicsInput>;
export type OnboardingGoalInput = z.input<typeof onboardingGoalInput>;

/**
 * Uputa za mjerenje kakvu VIDI onaj tko je uređuje (trener ili admin).
 *
 * Razlikuje se od `MeasurementGuide` koju dobiva klijent: ondje su potpisane
 * adrese snimki koje vrijede sat vremena, ovdje je i `videoKey` — ime objekta u
 * spremištu. Bez ključa se uređivanje ne može spremiti a da ne izgubi snimku:
 * potpisana adresa istekne, pa bi je spremanje upisalo kao mrtvu poveznicu.
 */
export interface MeasurementGuideAdminStep {
  key: string;
  hr: string;
  en: string;
  videoKey: string | null;
  /** Za pregled u pregledniku; ne sprema se natrag. */
  videoUrl: string | null;
}

export interface MeasurementGuideAdmin {
  steps: MeasurementGuideAdminStep[];
}

/**
 * Tocke mjerenja koje aplikacija zna povezati s poljem obrasca
 * (`FIELD_BY_STEP` u mobilnoj). Popis je zatvoren namjerno: slobodan kljuc bi
 * dao uputu koja nigdje ne izlazi, a to se ne vidi dok netko ne prijavi da mu
 * „nema upute za struk".
 */
export const measurementStepKeys = ['weight', 'waist', 'chest', 'hips', 'arm', 'thigh', 'body_fat'] as const;
export type MeasurementStepKey = (typeof measurementStepKeys)[number];

export const measurementGuideStepInput = z.object({
  key: z.enum(measurementStepKeys),
  hr: z.string().trim().max(600),
  en: z.string().trim().max(600),
  /** `null` znaci „bez snimke"; postojeci kljuc se salje natrag nepromijenjen. */
  videoKey: z.string().trim().max(300).nullable(),
});

export const measurementGuideInput = z.object({
  steps: z.array(measurementGuideStepInput).max(measurementStepKeys.length),
});

export const measurementVideoUrlInput = z.object({
  stepKey: z.enum(measurementStepKeys),
  contentType: z.enum(['video/mp4', 'video/webm']),
});

export type MeasurementGuideStepInput = z.infer<typeof measurementGuideStepInput>;
export type MeasurementGuideInput = z.infer<typeof measurementGuideInput>;
export type MeasurementVideoUrlInput = z.infer<typeof measurementVideoUrlInput>;
